/**
 * Which part of the page shows one wall's handwriting (plan 4 §5).
 *
 * The excerpt is the person's own drawing beside the question — „gelesen:
 * 17,5“ next to the place it was written. Geometry only: it crops, it never
 * reads, and nothing it returns is a measurement.
 *
 * Three rules, and why:
 *
 * - **A band of ± 6 % of the page's longer side across the wall**, the same
 *   convention as the worker's wall strips (`wall_strips.py`, which use
 *   ± 12 % for the model). Half of the model's band: at ± 12 % a handwritten
 *   figure shrinks to a few pixels on a 72 px row. A figure written further
 *   out stays outside the excerpt — the Original pane shows the whole wall.
 * - **One scale for every excerpt**: the band is always 72 px across, so
 *   handwriting stands at the same size in every row. A long wall is not
 *   shrunk to fit, it is shown from its middle — where a figure usually is.
 * - **Never turned.** A vertical wall is shown upright, 72 px wide and at
 *   most 120 px high; rotating the sheet would rotate the handwriting too.
 */

export const EXCERPT_MARGIN = 0.06;
export const EXCERPT_HEIGHT_PX = 72;
export const EXCERPT_MAX_VERTICAL_PX = 120;
/** The list column is 22,5 rem; its rows keep 24 px of padding. */
export const EXCERPT_MAX_WIDTH_PX = 336;

export type PagePoint = readonly [number, number];

export interface ExcerptCut {
  /** The window into the page, in page pixels — an SVG `viewBox`. */
  view: { x: number; y: number; width: number; height: number };
  /** How large it is drawn, in CSS pixels. */
  display: { width: number; height: number };
  vertical: boolean;
}

export function excerptWindow(
  start: PagePoint,
  end: PagePoint,
  page: { width: number; height: number },
): ExcerptCut {
  const margin = EXCERPT_MARGIN * Math.max(page.width, page.height);
  const sx = start[0] * page.width;
  const sy = start[1] * page.height;
  const ex = end[0] * page.width;
  const ey = end[1] * page.height;
  const vertical = Math.abs(ey - sy) > Math.abs(ex - sx);
  const scale = EXCERPT_HEIGHT_PX / (2 * margin);

  // Along the wall: its own extent plus the band, cut to the cap around the
  // middle. Across: the band, centred on the wall.
  const alongLo = (vertical ? Math.min(sy, ey) : Math.min(sx, ex)) - margin;
  const alongHi = (vertical ? Math.max(sy, ey) : Math.max(sx, ex)) + margin;
  const cap = (vertical ? EXCERPT_MAX_VERTICAL_PX : EXCERPT_MAX_WIDTH_PX) / scale;
  const alongLength = Math.min(alongHi - alongLo, cap);
  const alongMiddle = (alongLo + alongHi) / 2;
  const along = slide(alongMiddle - alongLength / 2, alongLength, vertical ? page.height : page.width);

  const acrossMiddle = vertical ? (sx + ex) / 2 : (sy + ey) / 2;
  const across = slide(acrossMiddle - margin, 2 * margin, vertical ? page.width : page.height);

  const alongDisplay = alongLength * scale;
  return vertical
    ? {
        view: { x: across, y: along, width: 2 * margin, height: alongLength },
        display: { width: EXCERPT_HEIGHT_PX, height: alongDisplay },
        vertical,
      }
    : {
        view: { x: along, y: across, width: alongLength, height: 2 * margin },
        display: { width: alongDisplay, height: EXCERPT_HEIGHT_PX },
        vertical,
      };
}

/** Moves a window of `length` back inside `[0, size]` where it fits; a larger one stays centred. */
function slide(lo: number, length: number, size: number): number {
  if (length >= size) return lo;
  if (lo < 0) return 0;
  if (lo + length > size) return size - length;
  return lo;
}
